import {
  type Territory,
  useConfig,
} from "$global-providers/config-provider/provider";
import { TERRITORY } from "$routes/_lib/constants";
import { pick, prop, sortBy } from "ramda";
import React from "react";
import { defineMessage, type MessageDescriptor, useIntl } from "react-intl";

import {
  arabic,
  english,
  french,
  fulani,
  hausa,
  igbo,
  kamba,
  NIGERIAN_PATIENT_LANGUAGE_CODES,
  OTHER_LANGUAGE_CODE,
  other,
  PATIENT_LANGUAGE_CODES,
  spanishMexico,
  swahili,
  USER_LANGUAGE_CODES,
  yoruba,
  zabarmawa,
} from "./i18n-constants";

const languageUnknownMessage = defineMessage({
  id: "language.unknown",
  defaultMessage: "Unknown",
});

const nigerianLanguageMap = {
  [NIGERIAN_PATIENT_LANGUAGE_CODES.FULANI]: fulani,
  [NIGERIAN_PATIENT_LANGUAGE_CODES.HAUSA]: hausa,
  [NIGERIAN_PATIENT_LANGUAGE_CODES.IGBO]: igbo,
  [NIGERIAN_PATIENT_LANGUAGE_CODES.YORUBA]: yoruba,
  [NIGERIAN_PATIENT_LANGUAGE_CODES.ZABARMAWA]: zabarmawa,
};

export const orderLanguages = (
  _territory: Territory,
  languages: { name: string; code: string }[],
) => sortBy(prop("name"), languages);

const getLanguageForTerritory = ({
  languageMap,
  territory,
}: {
  languageMap: Record<string, MessageDescriptor>;
  territory: Territory;
}) => {
  switch (territory) {
    case TERRITORY.NIGERIA:
      return { ...languageMap, ...nigerianLanguageMap };
    case TERRITORY.LIBERIA:
      return pick([PATIENT_LANGUAGE_CODES.ENGLISH], languageMap);
    default:
      return languageMap;
  }
};

const useLanguageTranslation = (
  languageMap: Record<string, MessageDescriptor>,
  includeOther = false,
) => {
  const { territory } = useConfig();
  const { formatMessage } = useIntl();

  const langForTerritory = getLanguageForTerritory({ territory, languageMap });

  const translateLanguageCode = React.useCallback(
    (languageCode) =>
      formatMessage(langForTerritory[languageCode] || languageUnknownMessage),
    [formatMessage, langForTerritory],
  );

  const translatedLanguages = React.useMemo(
    () =>
      Object.keys(langForTerritory).map((code) => ({
        name: translateLanguageCode(code),
        code,
      })),
    [langForTerritory, translateLanguageCode],
  );

  const languages = React.useMemo(() => {
    const orderedLanguages = orderLanguages(territory, translatedLanguages);
    if (includeOther) {
      return [
        ...orderedLanguages,
        { name: formatMessage(other), code: OTHER_LANGUAGE_CODE },
      ];
    }

    return orderedLanguages;
  }, [formatMessage, includeOther, territory, translatedLanguages]);

  return { languages, translateLanguageCode };
};

const userLanguageMap = {
  [USER_LANGUAGE_CODES.ENGLISH]: english,
  [USER_LANGUAGE_CODES.FRENCH]: french,
  [USER_LANGUAGE_CODES.SPANISH_MEXICO]: spanishMexico,
};

const patientLanguagesMap = {
  [PATIENT_LANGUAGE_CODES.ARABIC]: arabic,
  [PATIENT_LANGUAGE_CODES.ENGLISH]: english,
  [PATIENT_LANGUAGE_CODES.FRENCH]: french,
  [PATIENT_LANGUAGE_CODES.KAMBA]: kamba,
  [PATIENT_LANGUAGE_CODES.SWAHILI]: swahili,
};

export const useUserLanguageTranslation = () =>
  useLanguageTranslation(userLanguageMap);
export const usePatientLanguageTranslation = () =>
  useLanguageTranslation(patientLanguagesMap, true);
